import { useState } from "react";
import { Button, Input, Modal, Select, Textarea } from "../../ui";
import { AMOUNTS, amountOf, amountsOf, formatMoney } from "../funding";
import { currencyOptionsOf, useOptions } from "../model";
import { fullName } from "./model";

const inputsOf = (funding) => ({ currency: funding?.currency ?? "", ...Object.fromEntries(AMOUNTS.map(({ key }) => [key, funding?.[key] == null ? "" : String(funding[key])])) });

/**
 * The loan approved for one student, next to what they asked for. On HEC's award list applying
 * changes the list on screen only; the DG and the Minister save it straight away, with a reason
 * (`withReason`).
 */
export default function ApprovedFundingDialog({ entry, onApply, onClose, withReason = false, busy = false }) {
  const requested = entry?.requestedFunding ?? {};
  const [form, setForm] = useState(() => inputsOf(entry?.approvedFunding ?? amountsOf(requested)));
  const { data: options } = useOptions();
  const [errors, setErrors] = useState({});
  const [reason, setReason] = useState("");

  const apply = () => {
    const found = {};
    if (!form.currency) found.currency = "Choose a currency";
    AMOUNTS.forEach(({ key, required }) => {
      const value = amountOf(form[key]);
      if (required && value == null) found[key] = "Required";
      else if (value != null && (!Number.isInteger(value) || value < 0)) found[key] = "Enter a whole amount of 0 or more";
    });
    if (Object.keys(found).length) return setErrors(found);
    onApply({ currency: form.currency, ...Object.fromEntries(AMOUNTS.map(({ key }) => [key, amountOf(form[key])])) }, reason.trim() || null);
  };

  const set = (name) => (e) => {
    setErrors((prev) => ({ ...prev, [name]: undefined }));
    setForm((prev) => ({ ...prev, [name]: e.target.value }));
  };

  return (
    <Modal
      open={Boolean(entry)}
      onClose={onClose}
      size="lg"
      title={entry ? `Loan for ${fullName(entry.applicant)}` : ""}
      footer={
        <>
          <Button variant="ghost" onClick={() => setForm(inputsOf(amountsOf(requested)))}>
            Use requested amounts
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={apply} loading={busy}>
            {withReason ? "Save amounts" : "Apply"}
          </Button>
        </>
      }
    >
      {entry && (
        <div className="flex flex-col gap-4">
          <Select label="Currency" required placeholder="Choose a currency" options={currencyOptionsOf(options?.currencies, entry?.approvedFunding?.currency)} value={form.currency} onChange={set("currency")} error={errors.currency} className="sm:w-64" />
          <div className="grid grid-cols-[1fr_auto_10rem] items-center gap-x-4 gap-y-3 text-sm">
            <span className="font-medium text-slate-500">Item</span>
            <span className="font-medium text-slate-500">Requested</span>
            <span className="font-medium text-slate-500">Approved</span>
            {AMOUNTS.map(({ key, label, required }) => (
              <div key={key} className="contents">
                <span className="text-slate-800">
                  {label}
                  {required && " *"}
                </span>
                <span className="text-right text-slate-600">{formatMoney(requested[key], requested.currency)}</span>
                <Input type="number" min={0} step={1} aria-label={`Approved ${label.toLowerCase()}`} value={form[key]} onChange={set(key)} error={errors[key]} />
              </div>
            ))}
          </div>
          {withReason && <Textarea label="Reason (optional)" rows={2} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} />}
        </div>
      )}
    </Modal>
  );
}
